// src/pages/Users/Techniques/components/TechniqueCard.jsx

import React from "react";
import { FaEye, FaHeart, FaRegHeart, FaVolumeUp } from "react-icons/fa";

import { toAbsoluteTechniquePhotoUrl } from "../../../../services/techniquesPhotoServices";

import useTechniqueSpeech from "../hooks/useTechniqueSpeech";
import { speakJapanese } from "../utils/speechUtils";
import {
  FALLBACK_TECHNIQUE_IMG,
  beltTheme,
  truncateText,
} from "../utils/techniquesUtils";

function onImageError(event) {
  if (event?.currentTarget) {
    event.currentTarget.src = FALLBACK_TECHNIQUE_IMG;
  }
}

export default function TechniqueCard({
  technique,
  isFavorite,
  onToggleFavorite,
  onOpen,
}) {
  const {
    image1_url,
    nom,
    famille,
    traduction,
    ceinture,
    information,
  } = technique || {};

  const imgSrc =
    toAbsoluteTechniquePhotoUrl(image1_url) || FALLBACK_TECHNIQUE_IMG;

  const theme = beltTheme(ceinture);
  const speechText = useTechniqueSpeech(nom);

  const infoPreview = truncateText(information, 105);

  const handleSpeak = (event) => {
    event.stopPropagation();
    speakJapanese(speechText);
  };

  const handleFavorite = (event) => {
    event.stopPropagation();
    onToggleFavorite();
  };

  return (
    <article className="group overflow-hidden rounded-[1.65rem] border border-slate-200 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-xl hover:shadow-slate-950/10 dark:border-slate-800 dark:bg-slate-900">
      <button
        type="button"
        onClick={onOpen}
        className="relative block w-full bg-slate-100 text-left dark:bg-slate-950"
        aria-label={`Ouvrir la technique ${nom || ""}`}
      >
        <div className="aspect-[551/617] w-full overflow-hidden">
          <img
            src={imgSrc}
            alt={nom || "Technique de judo"}
            className="h-full w-full object-contain p-1 transition duration-300 group-hover:scale-[1.03]"
            loading="lazy"
            onError={onImageError}
          />
        </div>

        <div className="absolute left-2 top-2">
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-bold uppercase ring-1 ${theme.badge}`}
          >
            {ceinture || "?"}
          </span>
        </div>

        <button
          type="button"
          onClick={handleFavorite}
          className={`absolute right-2 top-2 flex h-10 w-10 items-center justify-center rounded-2xl border border-white/60 bg-white/90 text-base shadow-lg backdrop-blur transition active:scale-95 dark:border-slate-700 dark:bg-slate-900/90 ${
            isFavorite
              ? "text-red-500"
              : "text-slate-500 hover:text-red-500"
          }`}
          aria-label={
            isFavorite ? "Retirer des favoris" : "Ajouter aux favoris"
          }
        >
          {isFavorite ? <FaHeart /> : <FaRegHeart />}
        </button>
      </button>

      <div className="p-3">
        <div className="min-h-[120px]">
          <h3 className="line-clamp-2 text-sm font-bold leading-snug text-slate-950 dark:text-white">
            {nom || "Technique sans nom"}
          </h3>

          {traduction && (
            <p className="mt-1 line-clamp-1 text-xs italic text-slate-500 dark:text-slate-400">
              {traduction}
            </p>
          )}

          {famille && (
            <p className="mt-1 line-clamp-1 text-[11px] font-semibold text-slate-400">
              {famille}
            </p>
          )}

          {infoPreview && (
            <p className="mt-2 line-clamp-3 text-xs leading-5 text-slate-600 dark:text-slate-300">
              {infoPreview}
            </p>
          )}
        </div>

        <div className="mt-3 grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={handleSpeak}
            className="inline-flex min-h-10 items-center justify-center gap-1.5 rounded-2xl bg-slate-100 px-2 text-xs font-bold text-slate-700 transition active:scale-95 dark:bg-slate-800 dark:text-slate-200"
          >
            <FaVolumeUp />
            Écouter
          </button>

          <button
            type="button"
            onClick={onOpen}
            className="inline-flex min-h-10 items-center justify-center gap-1.5 rounded-2xl bg-slate-950 px-2 text-xs font-bold text-white transition active:scale-95 dark:bg-white dark:text-slate-950"
          >
            <FaEye />
            Voir
          </button>
        </div>
      </div>
    </article>
  );
}